import type { FieldType, NodeFieldContent, SubProcessArg, SubProcessResolvedArg, TypeInstanceMap } from '@lighthouse/core'
import { find } from 'rambda'

import type { FindSingleRecordActionConfig, SettingRecordFields, UpdateRecordActionConfig } from '../../../types'
import { resolveFilter } from '../filterResolver'
import type { ExtraParams } from './common'
import { generateVariableValue } from './common'

/** 附件字段 API 期望 string[]；TEXT 解析结果可能是单串或逗号分隔 */
const coerceFileFieldValue = (v: unknown): string[] => {
    if (v === undefined || v === null) {
        return []
    }
    if (Array.isArray(v)) {
        return v.map(String).filter(Boolean)
    }
    if (typeof v === 'string') {
        const parts = v
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        return parts.length > 0 ? parts : []
    }
    return [String(v)]
}

export const generateFieldVariableValue = (
    innerType: TypeInstanceMap,
    field: NodeFieldContent,
    extraParams: ExtraParams,
    fieldType?: FieldType
) => {
    const { fieldId, value } = field

    let v = generateVariableValue({ innerType, jsonContent: value, extraParams })

    if (fieldType === 'file' && v !== undefined) {
        v = coerceFileFieldValue(v)
    }

    return {
        fieldId,
        value: v
    }
}

export const generateArgVariableValue = (arg: SubProcessArg, extraParams: ExtraParams): SubProcessResolvedArg => {
    const { value, innerType } = arg

    const v = value ? generateVariableValue({ innerType, jsonContent: value, extraParams }) : undefined

    return {
        ...arg,
        value: v
    }
}

export const getCreateRecordVariableValue = (config: SettingRecordFields, extraParams: ExtraParams) => {
    const { fields, dataSourceId } = config
    const dataSource = find(item => item.id === dataSourceId, extraParams?.dataSourceList || [])

    return fields.map(field => {
        const fp = dataSource?.schema[field.fieldId]
        return generateFieldVariableValue(fp?.innerType || 'TEXT', field, extraParams, fp?.type)
    })
}

export const getUpdateRecordVariableValue = (dataSourceId: string, config: UpdateRecordActionConfig, extraParams: ExtraParams) => {
    const { fields } = config
    const dataSource = find(item => item.id === dataSourceId, extraParams?.dataSourceList || [])
    const fieldsParams = fields.map(field => {
        const fp = dataSource?.schema[field.fieldId]
        return generateFieldVariableValue(fp?.innerType || 'TEXT', field, extraParams, fp?.type)
    })

    return {
        fields: fieldsParams
    }
}

export const getFindSingleRecordVariableValue = (
    config: Pick<FindSingleRecordActionConfig, 'dataSourceId' | 'sorts' | 'filter'>,
    extraParams: ExtraParams
) => {
    const { dataSourceId, sorts, filter } = config
    const { appId, envId } = extraParams
    const resolvedFilter = resolveFilter({ filter, extraParams })

    return {
        appId,
        envId,
        dsId: dataSourceId,
        sorts,
        filter: resolvedFilter
    }
}
